package com.example.monetix.utils

import com.example.monetix.stripSystemMarker

/**
 * LOGICA RADICALE LOGHI:
 * Chiamate dirette senza proxy, per evitare blocchi anti-bot.
 */

private fun buildBestLogoUrlForDomain(domain: String?): String? {
    val cleanDomain = domain?.trim()?.lowercase()?.ifBlank { null } ?: return null
    // Chiamata diretta e pulita a Clearbit
    return "https://logo.clearbit.com/$cleanDomain"
}

fun getFallbackFaviconUrl(name: String?): String? {
    val domain = autoDomainForName(name) ?: return null
    // Chiamata diretta a Google Favicons
    return "https://www.google.com/s2/favicons?sz=128&domain=$domain"
}

private fun normalizeKey(value: String?): String {
    return value
        .orEmpty()
        .trim()
        .lowercase()
        .replace("à", "a")
        .replace("è", "e")
        .replace("é", "e")
        .replace("ì", "i")
        .replace("ò", "o")
        .replace("ù", "u")
        .replace(Regex("[^a-z0-9 ]"), " ")
        .replace(Regex("\\s+"), " ")
        .trim()
}

// Mappa dei domini per i mercanti più comuni
val MERCHANT_DOMAIN_MAP: Map<String, String> = mapOf(
    "lidl" to "lidl.it",
    "conad" to "conad.it",
    "esselunga" to "esselunga.it",
    "carrefour" to "carrefour.it",
    "coop" to "e-coop.it",
    "amazon" to "amazon.it",
    "poste" to "poste.it",
    "paypal" to "paypal.com",
    "unicredit" to "unicredit.it",
    "intesa" to "intesasanpaolo.com",
    "eurospin" to "eurospin.it",
    "md" to "mdspa.it",
    "despar" to "despar.it",
    "mcdonald" to "mcdonalds.com",
    "burger king" to "burgerking.it",
    "netflix" to "netflix.com",
    "spotify" to "spotify.com",
    "youtube" to "youtube.com",
    "tim" to "tim.it",
    "vodafone" to "vodafone.it",
    "enel" to "enel.it",
    "revolut" to "revolut.com",
    "telepass" to "telepass.com",
    "apple" to "apple.com",
    "google" to "google.com",
    "satispay" to "satispay.com",
    "iliad" to "iliad.it"
)

fun autoDomainForName(name: String?): String? {
    val raw = name?.trim()?.lowercase() ?: return null
    if (raw.isBlank()) return null

    val cleaned = stripSystemMarker(raw) ?: raw
    val normalized = normalizeKey(cleaned)

    val looksLikeDomain = cleaned.contains('.') && !cleaned.contains(' ') && cleaned.length >= 4
    if (looksLikeDomain) return cleaned

    for ((keyword, domain) in MERCHANT_DOMAIN_MAP) {
        if (normalized.contains(keyword)) {
            return domain
        }
    }

    val compact = normalized.replace(" ", "")
    if (compact.length in 3..24) {
        return "$compact.it"
    }

    return null
}

fun autoFaviconUrlForName(name: String?): String? {
    val domain = autoDomainForName(name) ?: return null
    return buildBestLogoUrlForDomain(domain)
}

fun resolveLogoUriOrNull(logoEmoji: String?, logoUri: String?, fallbackName: String?): String? {
    if (!logoEmoji.isNullOrBlank()) return null
    if (!logoUri.isNullOrBlank()) return logoUri
    return autoFaviconUrlForName(fallbackName)
}

fun extractFinancingIdFromMarker(s: String?): String? {
    if (s.isNullOrBlank()) return null
    val m = Regex("\\[fin:([^:\\]]+):").find(s) ?: return null
    return m.groupValues.getOrNull(1)?.trim()?.ifBlank { null }
}